import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bot, CalendarOff, FolderOpen, Globe, Handshake, Inbox, MessageSquare, MessageSquarePlus, ShieldCheck, Smartphone, Sparkles } from "lucide-react";
import CreateSessionButton from "./button/CreateSessionButton";
import FeedbackModal from "./feedback/FeedbackModal";
import GetTheAppDialog from "./app-stores/GetTheAppDialog";
import {
  AttendanceNavIcon,
  BillingNavIcon,
  CalendarNavIcon,
  HomeNavIcon,
  MembersNavIcon,
  NAV_STROKE_WIDTH,
  SessionsNavIcon,
} from "./navigation/NavIcons";
import { ROUTES } from "../routes";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  selectIsAdmin,
  selectIsOrgOwnerOrAdmin,
} from "../features/authentication/authenticationSlice";
import { selectInboxCount } from "../features/trial-requests/trial-requests-slice";
import { fetchInboxCount } from "../features/trial-requests/slice-extensions/trial-requests-slice-extension";
import { useUnreadCount } from "../features/messages/hooks/useUnreadCount";
import { STAFF_CHANNEL } from "../features/messages/models/MessagesChannel";

interface NavItem {
  route: string;
  icon: React.ReactNode;
  label: string;
  badge?: number;
}

/**
 * Desktop navigation, grouped by job so the growing item list stays scannable:
 * Home, then Schedule / People / Business sections, with one-time tools and
 * rare destinations (Import, Admin, feedback) demoted below the divider.
 */
const LeftMenu: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(selectIsAdmin);
  const isOrgOwnerOrAdmin = useAppSelector(selectIsOrgOwnerOrAdmin);
  const inboxCount = useAppSelector(selectInboxCount);
  const unreadMessages = useUnreadCount(STAFF_CHANNEL);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [getAppOpen, setGetAppOpen] = useState(false);

  // Refresh the Inbox badge on every navigation so it stays honest after
  // requests are handled (the counts are three cheap COUNT queries).
  useEffect(() => {
    dispatch(fetchInboxCount());
  }, [dispatch, location.pathname]);

  const isActiveRoute = (route: string) => {
    if (route === ROUTES.HOME_PAGE) {
      return location.pathname === ROUTES.HOME_PAGE || location.pathname === ROUTES.HOME;
    }
    const prefixRoutes = [
      ROUTES.CALENDAR,
      ROUTES.ATTENDANCE,
      ROUTES.SESSIONS,
      ROUTES.MEMBERS,
      ROUTES.MESSAGES,
      ROUTES.BILLING,
      ROUTES.ADMIN_USERS,
    ];
    if (prefixRoutes.includes(route)) {
      return location.pathname.startsWith(route === ROUTES.ADMIN_USERS ? "/admin" : route);
    }
    return location.pathname === route;
  };

  const schedule: NavItem[] = [
    { route: ROUTES.CALENDAR, icon: <CalendarNavIcon />, label: t("navigation.calendar") },
    { route: ROUTES.ATTENDANCE, icon: <AttendanceNavIcon />, label: t("navigation.attendance") },
    { route: ROUTES.SESSIONS, icon: <SessionsNavIcon />, label: t("navigation.sessions") },
    {
      route: ROUTES.ABSENCES,
      icon: <CalendarOff size={24} strokeWidth={NAV_STROKE_WIDTH} />,
      label: t("navigation.absences", "Absences"),
    },
  ];

  const people: NavItem[] = [
    { route: ROUTES.MEMBERS, icon: <MembersNavIcon />, label: t("navigation.members") },
    {
      route: ROUTES.MESSAGES,
      icon: <MessageSquare size={24} strokeWidth={NAV_STROKE_WIDTH} />,
      label: t("messages.nav.messages"),
      badge: unreadMessages,
    },
    {
      route: ROUTES.TRIAL_REQUESTS,
      icon: <Inbox size={24} strokeWidth={NAV_STROKE_WIDTH} />,
      label: t("navigation.trialRequests"),
      badge: inboxCount,
    },
    {
      route: ROUTES.MATERIALS,
      icon: <FolderOpen size={24} strokeWidth={NAV_STROKE_WIDTH} />,
      label: t("navigation.materials", "Materials"),
    },
  ];

  const business: NavItem[] = [
    {
      route: ROUTES.WEBSITE,
      icon: <Globe size={24} strokeWidth={NAV_STROKE_WIDTH} />,
      label: t("navigation.website"),
    },
    ...(isOrgOwnerOrAdmin
      ? [{ route: ROUTES.BILLING, icon: <BillingNavIcon />, label: t("navigation.billing") }]
      : []),
    {
      route: ROUTES.REFERRAL_PROGRAM,
      icon: <Handshake size={24} strokeWidth={NAV_STROKE_WIDTH} />,
      label: t("navigation.referrals"),
    },
  ];

  const renderItem = (item: NavItem) => (
    <li key={item.route}>
      <Link
        to={item.route}
        className={`menu-link py-1.5 ${isActiveRoute(item.route) ? "active" : ""}`}
      >
        {item.icon}
        {item.label}
        {item.badge != null && item.badge > 0 && (
          <span className="ml-auto rounded-full bg-primary-40 px-2 py-0.5 text-xs font-semibold text-white dark:bg-primary-90 dark:text-primary-10">
            {item.badge > 9 ? "9+" : item.badge}
          </span>
        )}
      </Link>
    </li>
  );

  const renderGroup = (label: string, items: NavItem[]) => (
    <div className="mt-2">
      <p className="mb-0.5 px-3 text-xs font-semibold uppercase tracking-wider text-grey-50 dark:text-grey-60">
        {label}
      </p>
      <ul className="w-full space-y-1">{items.map(renderItem)}</ul>
    </div>
  );

  return (
    // Capped just under the viewport (header offset + breathing room) so a short window
    // scrolls the menu itself — a sticky element taller than the screen would
    // otherwise leave its bottom items unreachable. Row/group spacing is tuned so the
    // full menu fits a 900px-tall laptop screen without showing that scrollbar.
    <div className="sticky top-20 left-0 hidden max-h-[calc(100vh-6rem)] shrink-0 overflow-y-auto overscroll-contain [scrollbar-width:thin] lg:block">
      <div className="w-full min-w-[144px] shrink-0 px-10 py-2 pb-4">
        <nav className="w-full flex-1 shrink-0" role="menu">
          <ul className="w-full space-y-1">
            {renderItem({
              route: ROUTES.HOME_PAGE,
              icon: <HomeNavIcon />,
              label: t("navigation.home"),
            })}
          </ul>

          {renderGroup(t("navigation.groupSchedule", "Schedule"), schedule)}
          {renderGroup(t("navigation.groupPeople", "People"), people)}
          {renderGroup(t("navigation.groupBusiness", "Business"), business)}
        </nav>

        <hr className="border-grey-70 my-2" />

        {/* Rarely used tools: a compact icon row (tooltips carry the labels)
            instead of four full-width rows — keeps the menu on one screen. */}
        <div className="flex justify-center gap-1">
          <Link
            to={ROUTES.ASSISTANT}
            className={`menu-link ${isActiveRoute(ROUTES.ASSISTANT) ? "active" : ""}`}
            title={t("navigation.assistant")}
            aria-label={t("navigation.assistant")}
          >
            <Bot size={20} strokeWidth={NAV_STROKE_WIDTH} />
          </Link>
          <Link
            to={ROUTES.ASSISTANT_IMPORT}
            className={`menu-link ${isActiveRoute(ROUTES.ASSISTANT_IMPORT) ? "active" : ""}`}
            title={t("navigation.import")}
            aria-label={t("navigation.import")}
          >
            <Sparkles size={20} strokeWidth={NAV_STROKE_WIDTH} />
          </Link>
          {isAdmin && (
            <Link
              to={ROUTES.ADMIN_USERS}
              className={`menu-link ${isActiveRoute(ROUTES.ADMIN_USERS) ? "active" : ""}`}
              title={t("navigation.admin")}
              aria-label={t("navigation.admin")}
            >
              <ShieldCheck width={20} height={20} className="text-grey-50 dark:text-grey-60" />
            </Link>
          )}
          <button
            type="button"
            className="menu-link"
            onClick={() => setFeedbackOpen(true)}
            title={t("navigation.sendFeedback")}
            aria-label={t("navigation.sendFeedback")}
          >
            <MessageSquarePlus width={20} height={20} className="text-grey-50 dark:text-grey-60" />
          </button>
          <button
            type="button"
            className="menu-link"
            onClick={() => setGetAppOpen(true)}
            title={t("getTheApp.title")}
            aria-label={t("getTheApp.title")}
          >
            <Smartphone width={20} height={20} className="text-grey-50 dark:text-grey-60" />
          </button>
        </div>

        <div className="mt-3 flex justify-center">
          <CreateSessionButton text={t("navigation.newSession")} />
        </div>
      </div>

      {feedbackOpen && <FeedbackModal onClose={() => setFeedbackOpen(false)} />}
      {getAppOpen && <GetTheAppDialog onClose={() => setGetAppOpen(false)} />}
    </div>
  );
};

export default LeftMenu;
